import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { spacing } from '@tq/ui-components/dist/components/mobile-options-accordion/styles/theme/spacing';
import {
  BoxProps,
  Checkbox,
  DetailsCardHeader,
  Grid,
  Typography,
  LinkButton,
  useThemedValueSwitch,
} from '@tq/ui-components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { ConsultationCardMenu, ConsultationStatusIndicator } from 'components';
import { useNavigate } from 'hooks';
import { selectedConsultationIdsState } from 'store';
import {
  CARD_LEFT_PADDING_SIZE,
  CARD_RIGHT_PADDING_SIZE,
  CHECKBOX_SIZE,
  ROW_TEMPLATE_COLUMNS_DESKTOP,
} from '../../constants';
import { ConsultationCardProps } from '../../types';
import { renderCardLabelledValue } from '../../util';
import { useFormatLocaleDate } from 'config/DateFormats/useFormatLocaleDate';

const FIRST_ROW_DESKTOP_TEMPLATE = `
"checkbox link status ellipsis"
/ ${CHECKBOX_SIZE} auto auto ${spacing(5)}`;

const FIRST_ROW_MOBILE_TEMPLATE = `
"checkbox status ellipsis"
"link link link"
/ ${CHECKBOX_SIZE} auto ${spacing(5)}`;

const MOBILE_LABEL_LAYOUT: BoxProps = {
  display: 'grid',
  gridAutoFlow: 'column',
  gridTemplateColumns: '120px auto',
};

export const ConsultationCardHeader: React.FC<ConsultationCardProps> = ({ consultation }) => {
  const { t } = useTranslation('consultation_card');
  const { navigateToSummary } = useNavigate();
  const { tvs } = useThemedValueSwitch();
  const formatLocaleDate = useFormatLocaleDate();

  const isMobile = useIsMobile();
  const [selectedConsultationIds, setSelectedConsultationIds] = useRecoilState(
    selectedConsultationIdsState
  );

  const handleSelectChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const consultationId = +event.target.value;
    if (event.target.checked) {
      setSelectedConsultationIds([...selectedConsultationIds, consultationId]);
    } else {
      setSelectedConsultationIds(
        selectedConsultationIds.filter((id) => id !== consultationId)
      );
    }
  };

  return (
    <DetailsCardHeader
      display='block'
      pr={CARD_RIGHT_PADDING_SIZE}
      sx={tvs(
        {
          tq: {
            p: 5,
            py: 3,
          },
        },
        {}
      )}
    >
      <Grid
        gridTemplate={{ base: FIRST_ROW_MOBILE_TEMPLATE, lg: FIRST_ROW_DESKTOP_TEMPLATE }}
        gridTemplateColumns={`${CARD_LEFT_PADDING_SIZE} auto auto ${spacing(5)}`}
        gridAutoFlow='column'
        alignItems='center'
      >
        <Checkbox
          mt={-1}
          gridArea='checkbox'
          value={consultation.id}
          isChecked={selectedConsultationIds.includes(consultation.id)}
          onChange={handleSelectChanged}
          onClick={(event) => event.stopPropagation()}
          aria-label={t('consultation_card_checkbox', 'Consultation card checkbox')}
          data-testid='consultation-card-checkbox'
        />

        <LinkButton
          gridArea='link'
          mt={{ base: 3, lg: 0 }}
          w='fit-content'
          textAlign='left'
          onClick={(e) => {
            e.stopPropagation();
            navigateToSummary(consultation.id);
          }}
        >
          {consultation.address}
        </LinkButton>

        <ConsultationStatusIndicator
          gridArea='status'
          justifySelf={{ base: 'start', lg: 'end' }}
          mr={3}
          consultation={consultation}
          screenReaderLabel={`${t('consultation_status', 'Consultation status')}: ${
            consultation?.status
          }`}
        />

        <ConsultationCardMenu consultation={consultation} />
      </Grid>
      <Grid
        ml={{ base: 'unset', lg: CARD_LEFT_PADDING_SIZE }}
        mt={2}
        gridGap={3}
        gridAutoFlow={{ base: 'row', lg: 'column' }}
        gridTemplateColumns={{ base: 'auto', lg: ROW_TEMPLATE_COLUMNS_DESKTOP }}
        justifyContent='space-between'
        alignItems='start'
      >
        <Typography>
          <strong>{consultation.authority}</strong>
        </Typography>
        {renderCardLabelledValue(
          t('reference', 'Reference'),
          consultation.reference,
          isMobile ? MOBILE_LABEL_LAYOUT : {}
        )}
        <Grid
          alignItems={{ base: 'start', lg: 'end' }}
          justifyContent={{ base: 'space-between' }}
          gridGap={{ base: 3, lg: 0 }}
          justifySelf={{ base: 'flex-start', lg: 'flex-end' }}
          justifyItems={tvs({ tq: 'end' }, '')}
        >
          {renderCardLabelledValue(
            t('requested', 'Requested'),
            formatLocaleDate(consultation.requestedDate),
            isMobile ? MOBILE_LABEL_LAYOUT : {},
            true
          )}
          {renderCardLabelledValue(
            t('deadline', 'Deadline'),
            formatLocaleDate(consultation.deadlineDate),
            isMobile ? MOBILE_LABEL_LAYOUT : {},
            true
          )}
        </Grid>
      </Grid>
    </DetailsCardHeader>
  );
};
